import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { motion } from 'framer-motion';

export default function DevVaultPrivacy() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="prose prose-slate max-w-none"
          >
            <h1 className="text-4xl font-bold text-slate-900 mb-4">DevVault Privacy Policy</h1>
            <p className="text-slate-500 font-medium mb-12">Last updated: May 26, 2026</p>

            <section className="mb-10 text-slate-700">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4 border-b pb-2">1. Overview</h2>
              <p className="leading-relaxed">
                DevVault is a Chrome extension designed to help developers securely organize and quickly use development and testing credentials, API keys, notes, code snippets, tenants, roles, environments, and related project information.
              </p>
              <p className="leading-relaxed mt-4">
                DevVault is intended for productivity purposes only and does not provide cloud services, analytics, advertising, or third-party data processing.
              </p>
            </section>

            <section className="mb-10 text-slate-700">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4 border-b pb-2">2. Data Storage</h2>
              <p className="leading-relaxed">
                DevVault stores user data locally within Chrome extension storage. Depending on the user’s Chrome settings, data may be synchronized between the user’s own devices using Google Chrome Sync (<code>chrome.storage.sync</code>).
              </p>
              <div className="mt-6 bg-slate-50 p-6 rounded-xl border border-slate-100">
                <p className="font-medium text-slate-800 mb-3">Stored data may include:</p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 list-disc pl-5">
                  <li>usernames</li>
                  <li>email addresses</li>
                  <li>passwords</li>
                  <li>API keys</li>
                  <li>URLs</li>
                  <li>notes</li>
                  <li>tags</li>
                  <li>project names</li>
                  <li>tenant names</li>
                  <li>roles</li>
                  <li>environments</li>
                  <li>code snippets</li>
                  <li>usage metadata (such as last used timestamps or usage counters)</li>
                </ul>
              </div>
              <p className="leading-relaxed mt-4">
                All stored information is managed solely by the user.
              </p>
            </section>

            <section className="mb-10 text-slate-700">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4 border-b pb-2">3. No External Servers</h2>
              <p className="leading-relaxed">
                DevVault does not transmit user vault data to any server operated by the developer. DevVault does not use:
              </p>
              <ul className="list-disc pl-5 mt-4 space-y-2">
                <li>custom backend servers</li>
                <li>cloud databases</li>
                <li>analytics platforms</li>
                <li>advertising SDKs</li>
                <li>tracking scripts</li>
                <li>third-party credential services</li>
              </ul>
              <p className="leading-relaxed mt-4">
                Any synchronization is handled exclusively by the user’s own Chrome account via Chrome Sync, if enabled by the user.
              </p>
            </section>

            <section className="mb-10 text-slate-700">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4 border-b pb-2">4. Website Interaction</h2>
              <p className="leading-relaxed">
                DevVault can interact with websites only when explicitly requested by the user. Examples include:
              </p>
              <ul className="list-disc pl-5 mt-4 space-y-2">
                <li>detecting the current website/domain</li>
                <li>capturing visible login field values</li>
                <li>filling saved credentials into login forms</li>
                <li>opening saved URLs</li>
              </ul>
              <p className="leading-relaxed mt-4">
                This interaction occurs only when the user activates extension features such as <strong>Capture Login</strong>, <strong>Fill</strong>, <strong>Open</strong>, or <strong>Open & Fill</strong>.
              </p>
              <p className="leading-relaxed mt-2">
                DevVault does not continuously monitor browsing activity and does not collect browsing history.
              </p>
            </section>

            <section className="mb-10 text-slate-700">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4 border-b pb-2">5. Permissions Used</h2>
              <div className="space-y-6">
                <div className="flex flex-col md:flex-row md:items-start gap-2">
                  <code className="bg-slate-100 px-2 py-1 rounded text-blue-700 font-bold min-w-[120px]">storage</code>
                  <p>Used to save vault data.</p>
                </div>
                <div className="flex flex-col md:flex-row md:items-start gap-2">
                  <code className="bg-slate-100 px-2 py-1 rounded text-blue-700 font-bold min-w-[120px]">activeTab</code>
                  <p>Used to access the currently active browser tab when the user interacts with the extension.</p>
                </div>
                <div className="flex flex-col md:flex-row md:items-start gap-2">
                  <code className="bg-slate-100 px-2 py-1 rounded text-blue-700 font-bold min-w-[120px]">tabs</code>
                  <p>Used to detect the current URL and open saved links.</p>
                </div>
                <div className="flex flex-col md:flex-row md:items-start gap-2">
                  <code className="bg-slate-100 px-2 py-1 rounded text-blue-700 font-bold min-w-[120px]">scripting</code>
                  <p>Used to inject scripts into the active page only when the user requests capture/fill actions.</p>
                </div>
                <div className="flex flex-col md:flex-row md:items-start gap-2">
                  <code className="bg-slate-100 px-2 py-1 rounded text-blue-700 font-bold min-w-[120px]">host permissions</code>
                  <p>Used so the extension can operate across multiple developer environments, SaaS domains, staging systems, and localhost projects.</p>
                </div>
              </div>
            </section>

            <section className="mb-10 text-slate-700">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4 border-b pb-2">6. Remote Code</h2>
              <p className="leading-relaxed">
                DevVault does not load remote executable code. All JavaScript, HTML, and CSS are packaged inside the extension. DevVault does not use remote JavaScript, WebAssembly from external sources, or eval-based downloaded code.
              </p>
            </section>

            <section className="mb-10 text-slate-700">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4 border-b pb-2">7. Data Sharing</h2>
              <p className="leading-relaxed">
                DevVault does not sell, rent, transfer, or share user data with third parties. User data is not used for advertising, analytics, profiling, unrelated business purposes, or creditworthiness decisions.
              </p>
            </section>

            <section className="mb-10 text-slate-700">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4 border-b pb-2">8. User Control</h2>
              <p className="leading-relaxed">
                Users have full control over their data. Users may add, edit, or delete data, export/import JSON backups, or remove the extension at any time.
              </p>
            </section>

            <section className="mb-10 text-slate-700">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4 border-b pb-2">9. Security Notice</h2>
              <p className="leading-relaxed">
                DevVault is intended primarily for development and testing workflows. Users are responsible for deciding what information they store.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4 border-b pb-2">10. Contact</h2>
              <div className="bg-blue-50 p-8 rounded-2xl border border-blue-100">
                <p className="text-slate-700 text-lg">For support or questions regarding DevVault:</p>
                <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
                  <span className="font-bold text-slate-900">Developer Contact:</span>
                  <a 
                    href="mailto:support@codevaro.eu" 
                    className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold transition-colors"
                  >
                    support@codevaro.eu
                  </a>
                </div>
              </div>
            </section>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}